<template>
  <div v-if="value" class="give-badge-dialog-overlay" @click.self="handleOverlayClick">
    <div class="give-badge-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="featured-icon">
            <img :src="require('@components/assets/icons/badge.svg')" alt="badge icon" />
          </div>
        </div>
        <button class="close-button" @click="handleClose" aria-label="Close dialog">
          <Icon name="interface_close" :size="24" />
        </button>
      </div>

      <div class="text-content">
        <h2 class="dialog-title-modal">{{ dialogTitle }}</h2>
        <p v-if="isAppointMode" class="dialog-description appoint-description">
          {{ APPOINT_DIALOG_HEADER }}
        </p>
        <p v-if="description" class="dialog-description">{{ description }}</p>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <div v-if="employeeSelectable" class="give-badge-employee-field">
          <div class="give-badge-employee-label">Employee</div>
          <ExSelectPicker
            pickerLabel="Employee"
            label="Pick an Employee"
            searchLabel="Pick an Employee"
            placeholder="Search for Employee"
            :items="employeePickerItems"
            :value="selectedEmployee"
            :isEmployeePicker="true"
            :showSelectedEmployeeCard="true"
            :useCode="false"
            :useDescription="true"
            :enablePagination="true"
            :hasMoreData="employeesHasMore"
            :isLoadingMore="employeesLoading"
            :isInitialLoading="employeesLoading && employees.length === 0"
            :useBackendSearch="true"
            @item-selected="handleEmployeeSelected"
            @clear-selection="handleEmployeeCleared"
            @load-more="emit('employee-load-more')"
            @search="emit('employee-search', $event)"
          />
        </div>
        <div v-if="alreadyHasArchitect" class="give-badge-architect-message">
          <span class="give-badge-architect-text"
            >This employee already holds the Architect badge for this responsibility. No further badge
            can be issued.</span
          >
        </div>
        <BadgeLevelPicker
          v-else
          v-model="selectedLevel"
          :levels="filteredLevels"
          :lockAfterSelection="!!lockedLevel"
          @change="handleLevelChange"
        />

        <div v-if="!employeeSelectable || selectedEmployee" class="notify-section">
          <div v-if="effectiveCanNotify" class="notify-checkbox-container">
            <input
              type="checkbox"
              id="notify-employee"
              v-model="notifyEmployee"
              class="notify-checkbox"
            />
            <label for="notify-employee" class="notify-label"> Notify Employee </label>
          </div>
          <p v-if="effectiveCanNotify" class="notify-description">
            The employee will be notified via email
          </p>
          <div class="give-badge-notification-warning" v-if="!effectiveCanNotify">
            <img
              class="give-badge-warning-icon"
              :src="require('@components/assets/notify-outer.svg')"
              alt="warning icon"
              width="50"
              height="50"
              object-fit="contain"
            />
            <span v-if="effectiveAccessNeedsVerification" class="give-badge-warning-text"
              >Employee needs to be verified<br />
              before they can receive email notifications</span
            >
            <span v-else class="give-badge-warning-text"
              >Employee cannot be notified <br />
              because it does not have access</span
            >
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <div class="actions-content">
          <div class="actions-right">
            <StandardButton
              type="terminated"
              variant="primary"
              size="full-width"
              :label="submitLabel"
              :disabled="!isValid"
              :loaderLeft="loading"
              @click="handleGiveBadge"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import BadgeLevelPicker from "@components/BadgeLevelPicker/BadgeLevelPicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { APPOINT_DIALOG_HEADER } from "@components/utils/appointedBadge";

const LEVEL_ORDER = ["apprentice", "proficient", "coach", "architect"];

const ALL_LEVELS = [
  { value: "apprentice", label: "Apprentice", description: "can do with supervision" },
  { value: "proficient", label: "Proficient", description: "can do without supervision" },
  { value: "coach", label: "Coach", description: "can coach and inspect others" },
  { value: "architect", label: "Architect", description: "creates standards" },
];

/** Coerce API/UI variants to a picker tier (Path to Readiness / inspection parity). */
function coerceProficiencyLevel(raw) {
  const key = normalizeBadgeLevel(raw);
  return LEVEL_ORDER.includes(key) ? key : null;
}

const props = defineProps({
  /** "give" (default) or "appoint" — appoint emits "appoint-badge" and relabels the dialog. */
  mode: {
    type: String,
    default: "give",
    validator: (v) => ["give", "appoint"].includes(v),
  },
  value: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
    default: "",
  },
  employeeName: {
    type: String,
    default: "",
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  canNotify: {
    type: Boolean,
    default: true,
  },
  /** When `access` is null — show verification copy instead of generic "no access". */
  accessNeedsVerification: {
    type: Boolean,
    default: false,
  },
  defaultLevel: {
    type: [String, Number],
    default: "apprentice",
  },
  currentBadgeLevel: {
    type: [String, Number],
    default: null,
  },
  lockedLevel: {
    type: [String, Number],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Shows an employee picker above the level picker; "give-badge" then carries `employeeId`. */
  employeeSelectable: {
    type: Boolean,
    default: false,
  },
  /** Picker options: { id, fullName, firstName, lastName, title, avatar, access }. */
  employees: {
    type: Array,
    default: () => [],
  },
  employeesLoading: {
    type: Boolean,
    default: false,
  },
  employeesHasMore: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "input",
  "give-badge",
  "appoint-badge",
  "close",
  "employee-search",
  "employee-load-more",
  "employee-change",
]);

const normalizedDefaultLevel = computed(
  () => coerceProficiencyLevel(props.defaultLevel) || "apprentice"
);
const normalizedLockedLevel = computed(() => coerceProficiencyLevel(props.lockedLevel));
const normalizedCurrentBadgeLevel = computed(() => coerceProficiencyLevel(props.currentBadgeLevel));

const isAppointMode = computed(() => props.mode === "appoint");
const dialogTitle = computed(() =>
  isAppointMode.value ? "Appoint Badge for Responsibility" : "Give Badge for Responsibility"
);

const filteredLevels = computed(() => {
  if (normalizedLockedLevel.value) {
    const one = ALL_LEVELS.find((l) => l.value === normalizedLockedLevel.value);
    if (one) return [one];
    const fb =
      ALL_LEVELS.find((l) => l.value === normalizedDefaultLevel.value) ||
      ALL_LEVELS[0];
    return [fb];
  }
  if (!normalizedCurrentBadgeLevel.value) return isAppointMode.value ? ALL_LEVELS : null;
  const currentIndex = LEVEL_ORDER.indexOf(normalizedCurrentBadgeLevel.value);
  if (currentIndex < 0) return isAppointMode.value ? ALL_LEVELS : null;
  return ALL_LEVELS.filter((_, i) => i > currentIndex);
});

const submitLabel = computed(() => {
  if (!isAppointMode.value) return "Give Badge";
  const chosen = ALL_LEVELS.find((l) => l.value === selectedLevel.value);
  return chosen ? `Appoint as ${chosen.label}` : "Appoint";
});

const nextLevel = computed(() => {
  if (normalizedLockedLevel.value) return normalizedLockedLevel.value;
  if (normalizedCurrentBadgeLevel.value) {
    const currentIndex = LEVEL_ORDER.indexOf(normalizedCurrentBadgeLevel.value);
    if (currentIndex >= 0 && currentIndex < LEVEL_ORDER.length - 1) {
      return LEVEL_ORDER[currentIndex + 1];
    }
  }
  return normalizedDefaultLevel.value;
});

const selectedLevel = ref(nextLevel.value);
const notifyEmployee = ref(true);
const selectedEmployee = ref(null);

const employeePickerItems = computed(() =>
  props.employees.map((item) => ({ ...item, name: item.fullName, code: item.title }))
);

// Same rule as the Badge Holders tab: unknown access, suspended or no access cannot be notified.
const chosenEmployeeCanNotify = computed(() => {
  const access = selectedEmployee.value?.access;
  if (access == null) return false;
  if (access.is_suspended === true) return false;
  if (access.has_access === false) return false;
  return true;
});
const effectiveCanNotify = computed(() =>
  props.employeeSelectable ? chosenEmployeeCanNotify.value : props.canNotify
);
const effectiveAccessNeedsVerification = computed(() =>
  props.employeeSelectable
    ? selectedEmployee.value?.access == null
    : props.accessNeedsVerification
);

// Employee already has the highest level (Architect) — no further badge can be issued
const alreadyHasArchitect = computed(() => normalizedCurrentBadgeLevel.value === "architect");

const isValid = computed(() => {
  if (alreadyHasArchitect.value) return false;
  if (props.employeeSelectable && !selectedEmployee.value) return false;
  return (
    selectedLevel.value !== null && selectedLevel.value !== undefined && selectedLevel.value !== ""
  );
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        selectedLevel.value = nextLevel.value;
        notifyEmployee.value = props.canNotify;
      });
    } else {
      selectedEmployee.value = null;
    }
  }
);

watch(nextLevel, (newVal) => {
  if (props.value && newVal != null) {
    selectedLevel.value = newVal;
  }
});

const handleLevelChange = (level) => {
  selectedLevel.value = level;
};

watch(selectedEmployee, (employee) => emit("employee-change", employee));

const handleEmployeeSelected = (employee) => {
  selectedEmployee.value = employee || null;
};

const handleEmployeeCleared = () => {
  selectedEmployee.value = null;
  emit("employee-search", "");
};

const handleOverlayClick = () => {
  handleClose();
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleGiveBadge = () => {
  if (isValid.value) {
    const payload = {
      level: selectedLevel.value,
      notifyEmployee: notifyEmployee.value && effectiveCanNotify.value,
    };
    if (props.employeeSelectable) payload.employeeId = selectedEmployee.value.id;
    emit(isAppointMode.value ? "appoint-badge" : "give-badge", payload);
  }
};
</script>

<style scoped>
.give-badge-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.give-badge-notification-warning {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  flex-wrap: nowrap !important;
}

.give-badge-warning-icon {
  flex-shrink: 0 !important;
  width: 50px !important;
  height: 50px !important;
  object-fit: contain !important;
}

.give-badge-warning-text {
  font-family: Inter, sans-serif !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: 0 !important;
  color: #344054 !important;
  margin: 0 !important;
  white-space: nowrap !important;
  text-align: left !important;
}

.give-badge-dialog {
  background: white;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow:
    0px 8px 8px -4px rgba(16, 24, 40, 0.08),
    0px 20px 24px -4px rgba(16, 24, 40, 0.14);
  position: relative;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  padding: 24px 24px 0 24px;
  width: 100%;
  z-index: 1;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.featured-icon {
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
  margin-top: 16px;
  margin-bottom: 24px;
}

.dialog-title-modal {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  text-align: center;
  margin: 0;
}

.dialog-description {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: #475467;
  text-align: center;
  margin: 0;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(152, 162, 179, 0.1);
}

.dialog-content {
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.give-badge-employee-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.give-badge-employee-label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.give-badge-architect-message {
  padding: 12px 16px;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
}

.give-badge-architect-text {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #92400e;
}

.notify-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notify-checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notify-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid #7a5af8;
  background-color: #ffffff;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  position: relative;
  flex-shrink: 0;
}

.notify-checkbox:checked {
  background-color: #7a5af8;
  border-color: #7a5af8;
}

.notify-checkbox:checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

.notify-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notify-label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
  user-select: none;
}

.notify-description {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #98a2b3;
  margin: 0;
  margin-left: 28px;
}

.notify-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 28px;
  padding: 8px 12px;
  background-color: #fff4ed;
  border-radius: 8px;
}

.warning-icon {
  color: #f79009;
  flex-shrink: 0;
}

.warning-text {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #b93815;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0;
  width: 100%;
  margin-top: 32px;
}

.actions-content {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 24px 24px 24px;
  width: 100%;
}

.actions-right {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
}

.give-badge-button {
  display: flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-weight: bold;
  flex: 1 0 0;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--Black, #000);
  background: #000;
  color: #fff;
  /* Shadows/shadow-xs */
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.give-badge-button:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.give-badge-button:disabled {
  background-color: #d0d5dd;
  color: #98a2b3;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
